import {
  IconButton,
  TextInput,
  ColoredNextIcon as coloredNextIcon,
  ColoredPreviousIcon as coloredPreviousIcon,
  NextIcon as nextIcon,
  PreviousIcon as previousIcon,
} from '@/shared/ui';
import usePaginationContainer from './usePaginationContainer';

export type PaginationContainerProps = {
  activeButton: any;
  specialEventTypes: any;
  isDisabled?: boolean;
};

/**
 * Renders the page navigation strip for the ECG events tab, with previous and next buttons around an editable page-number input. Takes paging state and handlers from usePaginationContainer, hides itself entirely when isShowPagination is false, and disables each control from the computed edge flags or the isDisabled prop.
 *
 * @param props - Paging context for the hook plus the isDisabled flag applied to every control.
 * @returns The pagination row with its previous button, page input and next button.
 */
const PaginationContainer = ({
  activeButton,
  specialEventTypes,
  isDisabled,
}: PaginationContainerProps) => {
  const {
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    pageString,
    maxPageNumber,
    inputSize,
    isError,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
  } = usePaginationContainer({ activeButton, specialEventTypes, isDisabled });

  return (
    <div className="pagination-container">
      {isShowPagination && (
        <>
          <IconButton
            id="ecg-events-previous-page-btn"
            isFilled
            disabled={isDisabledPrev || isDisabled}
            iconComponent={
              <img src={isDisabledPrev ? previousIcon : coloredPreviousIcon} alt="Previous icon" />
            }
            onClick={onClickPrevious}
          />
          <TextInput
            id="ecg-events-page-input"
            ariaLabel="Page number"
            suffix={`/ ${maxPageNumber}`}
            name="pageString"
            value={pageString}
            onChange={onChangePageString}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            isNumberFormat
            shouldChangeSize
            className="page-input"
            size={inputSize}
            isError={isError}
            disabled={isDisabled}
            decimalSeparator={false}
          />
          <IconButton
            id="ecg-events-next-page-btn"
            isFilled
            disabled={isDisabledNext || isDisabled}
            iconComponent={
              <img src={isDisabledNext ? nextIcon : coloredNextIcon} alt="Next icon" />
            }
            onClick={onClickNext}
          />
        </>
      )}
    </div>
  );
};

export default PaginationContainer;
